import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Check, ChevronDown, ChevronUp, Loader2, Minus, Search, Users, X } from 'lucide-react';
import { SNAPCHAT_CONNECTION_TYPES, SNAPCHAT_OS_TYPES, defaultSnapTargeting } from './snapchatConfig';

const pathLabel = (item) => (item.path ? item.path.replace(/^\//, '').split('/').join(' > ') : item.name);

const Chip = ({ label, tone, onRemove, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${tone === 'exclude' ? 'bg-red-500/15 text-red-200 border border-red-500/30' : 'bg-green-500/15 text-green-200 border border-green-500/30'}`}>
    {tone === 'exclude' ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
    <span className="truncate max-w-[220px]">{label}</span>
    <button type="button" onClick={onRemove} className="hover:text-white"><X className="h-3 w-3" /></button>
  </span>
);

const ToggleChip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${active ? 'bg-[#FFFC00] text-black border-[#FFFC00] font-semibold' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>
    {children}
  </button>
);

const Section = ({ title, hint, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl bg-white/5 border border-white/10" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 text-left">
        <div>
          <p className="text-sm font-medium text-white">{title}</p>
          {hint && <p className="text-xs text-white/40">{hint}</p>}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-white/50" /> : <ChevronDown className="h-4 w-4 text-white/50" />}
      </button>
      {open && <div className="px-3 pb-3 space-y-3">{children}</div>}
    </div>
  );
};

const IncludeExcludePicker = ({ items, include, exclude, onChange, placeholder, labelFn = pathLabel, testId, emptyText }) => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? items.filter((i) => labelFn(i).toLowerCase().includes(q)) : items;
    return list.slice(0, 60);
  }, [items, query, labelFn]);
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const toggle = (id, mode) => {
    const inc = include.filter((x) => x !== id);
    const exc = exclude.filter((x) => x !== id);
    if (mode === 'include' && !include.includes(id)) inc.push(id);
    if (mode === 'exclude' && !exclude.includes(id)) exc.push(id);
    onChange(inc, exc);
  };
  return (
    <div className="space-y-2">
      {(include.length > 0 || exclude.length > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-selected`}>
          {include.map((id) => <Chip key={id} label={byId[id] ? labelFn(byId[id]) : id} onRemove={() => toggle(id, 'none')} testId={`${testId}-chip-${id}`} />)}
          {exclude.map((id) => <Chip key={id} tone="exclude" label={byId[id] ? labelFn(byId[id]) : id} onRemove={() => toggle(id, 'none')} testId={`${testId}-chip-${id}`} />)}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} className="pl-8 h-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm" data-testid={`${testId}-search`} />
      </div>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5" data-testid={`${testId}-list`}>
        {items.length === 0 ? (
          <p className="p-3 text-xs text-white/40">{emptyText}</p>
        ) : filtered.map((item) => {
          const inc = include.includes(item.id), exc = exclude.includes(item.id);
          return (
            <div key={item.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
              <span className={`truncate ${inc ? 'text-green-200' : exc ? 'text-red-200' : 'text-white/80'}`}>{labelFn(item)}</span>
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => toggle(item.id, inc ? 'none' : 'include')} className={`px-2 py-0.5 rounded ${inc ? 'bg-green-500 text-black' : 'bg-white/10 text-white/70 hover:bg-green-500/30'}`}>Include</button>
                <button type="button" onClick={() => toggle(item.id, exc ? 'none' : 'exclude')} className={`px-2 py-0.5 rounded ${exc ? 'bg-red-500 text-white' : 'bg-white/10 text-white/70 hover:bg-red-500/30'}`}>Exclude</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SnapchatTargetingPanel = ({ snap, value, onChange }) => {
  const t = { ...defaultSnapTargeting(), ...(value || {}) };
  const set = (patch) => onChange({ ...t, ...patch });
  const { targetingOptions: opts, segments, isLoadingTargeting, fetchTargetingOptions, fetchSegments, connection, selectedAccount } = snap;

  useEffect(() => {
    if (connection?.connected && !opts) fetchTargetingOptions('ng');
  }, [connection?.connected, opts, fetchTargetingOptions]);
  useEffect(() => {
    if (connection?.connected && selectedAccount?.id) fetchSegments(selectedAccount.id);
  }, [connection?.connected, selectedAccount?.id, fetchSegments]);

  const toggleIn = (key, val) => set({ [key]: t[key].includes(val) ? t[key].filter((x) => x !== val) : [...t[key], val] });

  if (!connection?.connected) {
    return (
      <div className="p-4 rounded-xl bg-[#FFFC00]/10 border border-[#FFFC00]/30 text-xs text-yellow-100" data-testid="snapchat-targeting-locked">
        Connect your Snapchat account to unlock detailed targeting (predefined audiences, custom audiences, languages, devices).
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="snapchat-targeting-panel">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#FFFC00] text-black flex items-center justify-center"><Users className="h-4 w-4" /></div>
        <div>
          <h3 className="font-semibold text-white text-sm">Detailed targeting (Snapchat)</h3>
          <p className="text-xs text-white/50">Same options as Snapchat Ads Manager — pulled live from the Marketing API</p>
        </div>
        {isLoadingTargeting && <Loader2 className="h-4 w-4 animate-spin text-white/50 ml-auto" />}
      </div>

      <Section title="Predefined audiences" hint="Snap Lifestyle Categories — include or exclude interests" testId="snapchat-predefined-audiences">
        <IncludeExcludePicker
          items={opts?.interests || []} include={t.interestsInclude} exclude={t.interestsExclude}
          onChange={(inc, exc) => set({ interestsInclude: inc, interestsExclude: exc })}
          placeholder="Search categories (e.g. Gamers, Music Fans, Sports)…" testId="snapchat-interests"
          emptyText={isLoadingTargeting ? 'Loading Snapchat categories…' : 'No categories returned by Snapchat for this country.'}
        />
        <div className="space-y-2 pt-1 border-t border-white/10">
          <label className="flex items-start gap-3 cursor-pointer">
            <Switch checked={t.autoExpansion} onCheckedChange={(v) => set({ autoExpansion: v, smartTargeting: v && t.smartTargeting })} data-testid="snapchat-auto-expansion-switch" />
            <span>
              <span className="text-sm text-white">Expand predefined audience inclusion automatically</span>
              <span className="block text-xs text-white/50">Reach Snapchatters beyond your included audiences when it’s likely to improve performance.</span>
            </span>
          </label>
          <label className={`flex items-start gap-3 cursor-pointer ${!t.autoExpansion ? 'opacity-40 pointer-events-none' : ''}`}>
            <Switch checked={t.smartTargeting} onCheckedChange={(v) => set({ smartTargeting: v })} data-testid="snapchat-smart-targeting-switch" />
            <span>
              <span className="text-sm text-white">Smart Targeting</span>
              <span className="block text-xs text-white/50">Also expand beyond gender and maximum age (never below minimum age).</span>
            </span>
          </label>
        </div>
      </Section>

      <Section title="Custom audiences" hint="Customer lists, pixel, engagement & lookalike audiences from this ad account" defaultOpen={false} testId="snapchat-custom-audiences">
        <IncludeExcludePicker
          items={segments.map((s) => ({ ...s, path: null }))} include={t.segmentsInclude} exclude={t.segmentsExclude}
          labelFn={(s) => `${s.name}${s.targetable_status && s.targetable_status !== 'READY' ? ` (${s.targetable_status.replace(/_/g, ' ').toLowerCase()})` : ''}`}
          onChange={(inc, exc) => set({ segmentsInclude: inc, segmentsExclude: exc })}
          placeholder="Search your audiences…" testId="snapchat-segments"
          emptyText="No custom audiences in this ad account yet. Create them in Snapchat Ads Manager → Audiences."
        />
      </Section>

      <Section title="Languages" hint="Only reach Snapchatters using these languages" defaultOpen={false} testId="snapchat-languages">
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {(opts?.languages || []).map((l) => (
            <ToggleChip key={l.id} active={t.languages.includes(l.id)} onClick={() => toggleIn('languages', l.id)} testId={`snapchat-language-${l.id}`}>{l.name}</ToggleChip>
          ))}
          {!opts?.languages?.length && <p className="text-xs text-white/40">{isLoadingTargeting ? 'Loading…' : 'No languages returned.'}</p>}
        </div>
      </Section>

      <Section title="Devices" hint="Operating system, connection type and carriers" defaultOpen={false} testId="snapchat-devices">
        <div className="space-y-2">
          <Label className="text-xs text-white/60">Operating system</Label>
          <div className="flex flex-wrap gap-1.5">
            {SNAPCHAT_OS_TYPES.map((os) => <ToggleChip key={os} active={t.osTypes.includes(os)} onClick={() => toggleIn('osTypes', os)} testId={`snapchat-os-${os}`}>{os}</ToggleChip>)}
          </div>
          <Label className="text-xs text-white/60">Connection type</Label>
          <div className="flex flex-wrap gap-1.5">
            {SNAPCHAT_CONNECTION_TYPES.map((c) => <ToggleChip key={c.value} active={t.connectionTypes.includes(c.value)} onClick={() => toggleIn('connectionTypes', c.value)} testId={`snapchat-connection-${c.value}`}>{c.label}</ToggleChip>)}
          </div>
          <Label className="text-xs text-white/60">Carriers</Label>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {(opts?.carriers || []).map((c) => <ToggleChip key={c.id} active={t.carrierIds.includes(c.id)} onClick={() => toggleIn('carrierIds', c.id)} testId={`snapchat-carrier-${c.id}`}>{c.name}</ToggleChip>)}
            {!opts?.carriers?.length && <p className="text-xs text-white/40">No carriers returned for this country.</p>}
          </div>
        </div>
      </Section>

      <Section title="States / regions" hint="Add whole states in addition to your globe geofence" defaultOpen={false} testId="snapchat-regions">
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {(opts?.regions || []).map((r) => <ToggleChip key={r.id} active={t.regionIds.includes(r.id)} onClick={() => toggleIn('regionIds', r.id)} testId={`snapchat-region-${r.id}`}>{r.name}</ToggleChip>)}
          {!opts?.regions?.length && <p className="text-xs text-white/40">No regions returned.</p>}
        </div>
      </Section>

      <label className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
        <Switch checked={t.regulatedContent} onCheckedChange={(v) => set({ regulatedContent: v })} data-testid="snapchat-regulated-switch" />
        <span>
          <span className="text-sm text-white">Regulated content</span>
          <span className="block text-xs text-white/50">Required for alcohol, gambling, red-band trailers and similar categories.</span>
        </span>
      </label>
    </div>
  );
};
